import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../components/notifications';
import LoadingSpinner from '../components/LoadingSpinner';
import AutoplannerTaskList from '../components/autoplanner/AutoplannerTaskList';
import AutoplannerWizard from '../components/autoplanner/AutoplannerWizard';
import { autoplannerAPI, trainingGroupsAPI } from '../services/api';

const Autoplanner = () => {
  const { user } = useAuth();
  const { showToast } = useNotifications();
  const [tasks, setTasks] = useState([]);
  const [trainingGroups, setTrainingGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [viewMode, setViewMode] = useState('my-tasks'); // 'my-tasks', 'all-tasks', 'future-only', 'all-dates'

  useEffect(() => {
    loadData();
  }, [viewMode]);

  const loadData = async () => {
    try {
      setLoading(true);

      const [tasksData, groupsData] = await Promise.all([
        autoplannerAPI.getAutoplannerTasks(viewMode),
        trainingGroupsAPI.getGroups(),
      ]);

      setTasks(tasksData);
      setTrainingGroups(groupsData || []);
    } catch (error) {
      console.error('Error loading autoplanner data:', error);
      const detail = error?.response?.data || error?.message || 'Unknown error';
      console.error('Error details:', detail);
      const toastMsg =
        typeof detail === 'string' ? detail : detail?.detail || 'Error loading autoplanner data';
      showToast(toastMsg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNewTask = () => {
    setSelectedTask(null);
    setShowWizard(true);
  };

  const handleEditTask = (task) => {
    setSelectedTask(task);
    setShowWizard(true);
  };

  const handleWizardComplete = async (isViewingCompletedTask = false) => {
    setShowWizard(false);
    setSelectedTask(null);
    await loadData(); // Refresh the task list

    // Only show success message if we're not viewing a completed task
    if (!isViewingCompletedTask) {
      showToast('Autoplanner task created successfully', 'success');
    }
  };

  const handleWizardCancel = async () => {
    setShowWizard(false);
    setSelectedTask(null);
    // Ensure task list reflects any draft task created before canceling
    await loadData();
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await autoplannerAPI.deleteAutoplannerTask(taskId);
      await loadData(); // Refresh the task list
      showToast('Task deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting task:', error);
      showToast('Failed to delete task', 'error');
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (showWizard) {
    return (
      <AutoplannerWizard
        trainingGroups={trainingGroups}
        selectedTask={selectedTask}
        onComplete={handleWizardComplete}
        onCancel={handleWizardCancel}
        advancedUser={user?.is_advanced_user}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Autoplanner</h1>
          <p className="text-gray-600">Create automated training schedules using AI</p>
        </div>
        <button
          onClick={handleCreateNewTask}
          className="inline-flex items-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          Create New Task
        </button>
      </div>

      {/* View Controls */}
      <div className="rounded-lg bg-white p-6 shadow">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center space-x-4">
            <label className="text-sm font-medium text-gray-700">View:</label>
            <select
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value)}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="my-tasks">My Tasks</option>
              <option value="all-tasks">All Tasks</option>
              <option value="future-only">Future Sessions Only</option>
              <option value="all-dates">All Dates</option>
            </select>
          </div>
        </div>
      </div>

      {/* Task List */}
      <AutoplannerTaskList
        tasks={tasks}
        onEditTask={handleEditTask}
        onRefresh={loadData}
        onDeleteTask={handleDeleteTask}
      />
    </div>
  );
};

export default Autoplanner;
